import { Root as CheckboxRoot } from '@radix-ui/react-checkbox';
import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';
import { XClose } from '@untitled-ui/icons-react';
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useOnClickOutside } from 'usehooks-ts';

import { removeElement } from '../shared/tiptap/dom-utilities';
import { Tooltip } from './tooltip';

const sizeClassNames = {
  xs: 'px-3 py-1',
  md: 'px-4 py-2',
};

const colorClassNames = {
  accentGradient:
    'bg-branding-accent-100 hover:bg-branding-accent-200 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-branding-accent-700 data-[state=checked]:to-purple-900 data-[state=checked]:hover:bg-branding-accent-high-contrast data-[state=checked]:text-white',
  gray: 'bg-branding-gray text-black group-hover:bg-branding-gray-200 data-[state=checked]:bg-branding-accent-high-contrast data-[state=checked]:hover:bg-branding-accent-high-contrast data-[state=checked]:text-white',
  darkBlue: 'bg-branding-accent-high-contrast text-white',
};

const colorClassInput = {
  accentGradient: 'bg-gradient-to-r from-branding-accent-600 to-purple-700 text-white',
  gray: 'bg-branding-accent-high-contrast text-white',
  darkBlue: 'bg-branding-accent-high-contrast text-white',
};

export type InputPillProps = {
  onClickOutside: () => unknown;
  value?: string;
  onChange: (value: string) => unknown;
  onClose?: () => unknown;
  placeholder?: string;
  size?: 'xs' | 'md';
  color?: 'accentGradient' | 'gray' | 'darkBlue';
};

export function InputPill(props: InputPillProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');
  const { color = 'gray', size = 'xs' } = props;

  const adjustWidth = useCallback(
    function () {
      if (inputRef?.current == null) {
        return;
      }
      const span = document.createElement('span');
      span.style.font = window.getComputedStyle(inputRef.current).font;
      span.style.position = 'absolute';
      span.style.visibility = 'hidden';
      span.style.whiteSpace = 'pre';
      span.textContent = value ?? props.placeholder ?? '';
      document.body.appendChild(span);
      const width = Math.max(40, span.offsetWidth + 10);
      inputRef.current.style.width = `${width}px`;
      removeElement(document.body, span);
    },
    [value, props.placeholder]
  );

  useEffect(
    function () {
      adjustWidth();
    },
    [value, adjustWidth]
  );

  useOnClickOutside(ref as unknown as RefObject<HTMLElement>, props.onClickOutside);

  return (
    <div
      className={twMerge(
        'flex items-center justify-between gap-2.5 rounded-full',
        colorClassInput[color],
        sizeClassNames[size]
      )}
      ref={ref}
    >
      <input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={function (e) {
          setValue(e.target.value);
          props.onChange(e.target.value);
        }}
        onKeyDown={function (e) {
          if (e.key === 'Enter') {
            props.onClickOutside();
          }
        }}
        type="text"
        className="font-body-2-r border-none bg-transparent text-white outline-none transition-all"
        style={{ minWidth: '40px' }}
        placeholder={props.placeholder}
      />
      {value.length > 0 ? (
        <div className="size-4 cursor-pointer text-white">
          <XClose
            width={16}
            height={16}
            onClick={function () {
              if (typeof props.onClose !== 'function') {
                return;
              }
              props.onClose();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

type TypeSinglePillProps = {
  children: ReactNode;
  onClose?: () => unknown;
  className?: string;
  size?: 'xs' | 'md';
  color?: 'accentGradient' | 'gray' | 'darkBlue';
};

export function Pill(props: TypeSinglePillProps) {
  const { children, onClose, color = 'gray', size = 'xs' } = props;
  return (
    <div
      className={twMerge(
        'font-body-2-r inline-flex items-center justify-between gap-2.5 rounded-full',
        colorClassNames[color],
        sizeClassNames[size]
      )}
    >
      {children}
      {onClose == null ? null : (
        <div className="size-4 cursor-pointer text-white">
          <XClose
            width={16}
            height={16}
            onClick={function () {
              if (typeof onClose !== 'function') {
                return;
              }
              onClose();
            }}
          />
        </div>
      )}
    </div>
  );
}

export type MultiSelectPillsProps = {
  items: string[];
  value: string[];
  children?: ReactNode;
  onChange: (value: string[]) => unknown;
  isNew?: boolean;
  size?: 'xs' | 'md';
  color?: 'accentGradient' | 'gray' | 'darkBlue';
};

export function MultiSelectPills(props: MultiSelectPillsProps) {
  const { isNew = false, size = 'xs', color = 'gray' } = props;
  return (
    <div className="flex flex-wrap gap-4">
      {props.items.map(function (item, index) {
        return (
          <div key={index} className="group">
            <CheckboxRoot
              name={item}
              checked={props.value.includes(item)}
              onCheckedChange={function (checked) {
                if (checked === true) {
                  props.onChange([...props.value, item]);
                } else {
                  props.onChange(props.value.filter((v) => v !== item));
                }
              }}
              className={twMerge(
                'font-body-2-r items-center justify-center rounded-full',
                colorClassNames[color],
                isNew &&
                  'data-[state=checked]:ring-4 data-[state=checked]:ring-branding-accent-200',
                sizeClassNames[size]
              )}
            >
              {item}
            </CheckboxRoot>
          </div>
        );
      })}
      {props.children != null ? props.children : null}
    </div>
  );
}

export type SingleSelectPillsProps = {
  items: { value: string; label: string; isDisabled?: boolean; tooltip?: string }[];
  value: string;
  children?: ReactNode;
  onChange: (value: string) => unknown;
  isNew?: boolean;
  size?: 'xs' | 'md';
  color?: 'accentGradient' | 'gray' | 'darkBlue';
  className?: string;
};

export function SingleSelectPills(props: SingleSelectPillsProps) {
  const { isNew = false, size = 'xs', color = 'gray', className = '' } = props;
  return (
    <RadioGroup
      className={twMerge('flex flex-wrap gap-2', className)}
      value={props.value}
      onValueChange={props.onChange}
    >
      {props.items.map(function (item, index) {
        if (item.tooltip != null) {
          return (
            <div key={index} className="group">
              <Tooltip
                text={item.tooltip}
                align="center"
                side="bottom"
                sideOffset={12}
                delayDuration={200}
                textClassName="text-center"
              >
                <span>
                  <RadioGroupItem
                    value={item.value}
                    disabled={item.isDisabled}
                    className={twMerge(
                      'font-body-2-r items-center justify-center rounded-full',
                      colorClassNames[color],
                      isNew &&
                        'data-[state=checked]:ring-4 data-[state=checked]:ring-branding-accent-200',
                      sizeClassNames[size],
                      item.isDisabled
                        ? 'cursor-default text-light-gray group-hover:bg-branding-gray'
                        : ''
                    )}
                  >
                    {item.label}
                  </RadioGroupItem>
                </span>
              </Tooltip>
            </div>
          );
        }
        return (
          <div key={index} className="group">
            <RadioGroupItem
              value={item.value}
              disabled={item.isDisabled}
              className={twMerge(
                'font-body-2-r items-center justify-center rounded-full',
                colorClassNames[color],
                isNew &&
                  'data-[state=checked]:ring-4 data-[state=checked]:ring-branding-accent-200',
                sizeClassNames[size]
              )}
            >
              {item.label}
            </RadioGroupItem>
          </div>
        );
      })}
      {props.children != null ? props.children : null}
    </RadioGroup>
  );
}
